import { Link } from "react-router-dom";
import { all_routes } from "../router/all_routes";
import CollapseHeader from "../../core/common/collapse-header/collapse-header";
import Table from "../../core/common/dataTable/index";
import { useState, useRef, useEffect } from "react";
import Swal from "sweetalert2";
import useApi from "../../core/customhook/useApi";
import MaxSelectTable from "../../core/common/MaxSelectTable";

const PreviewResume = () => {
  const [selectedInterviewerKeys, setSelectedInterviewerKeys] = useState<any[]>(
    [],
  );
  const [selectedInterviewerRows, setSelectedInterviewerRows] = useState<any[]>(
    [],
  );
  const [currentRecord, setCurrentRecord] = useState<any>(null);

  //  Store interviewers per resume_id (no API call on select)
  const [interviewerMap, setInterviewerMap] = useState<Record<string, any[]>>(
    {},
  );

  const closeBtnRef = useRef<HTMLButtonElement>(null);

  const { data: employeeList } = useApi(`/api/select-interviewer`, {
    method: "GET",
    autoCall: true,
  });

  const { data: resumeList, refetch: refetchResumes } = useApi(
    "/api/candidate-selection",
    {
      method: "GET",
      autoCall: true,
    },
  );

  useEffect(() => {
    if (!resumeList?.data) return;

    const map: Record<string, any[]> = {};
    resumeList.data.forEach((item: any) => {
      if (item.interviewer && item.interviewer.length > 0) {
        map[item.resume_id] = item.interviewer; // resume_id: ["T1-9080", "T1-9065", ...]
      }
    });

    setInterviewerMap(map);
  }, [resumeList]);
  // const { callApi: SelectCvApi, loading: submitting } = useApi("/api/add-interviewer", {
  //   method: "POST",
  //   autoCall: false,
  // });

  const { callApi: updateCvStatus, loading: statusUpdating } = useApi(
    "/api/cv-status",
    {
      method: "PUT",
      autoCall: false,
    },
  );

  const { callApi: SelectCvApi, loading: submitting } = useApi(
    "/api/select-resume",
    {
      method: "POST",
      autoCall: false,
    },
  );

  const handleOpenModal = (record: any) => {
    setCurrentRecord(record);
    // Pre-fill already selected interviewers for this CV if any
    const existing = interviewerMap[record.resume_id] ?? [];
    setSelectedInterviewerKeys(existing);
    setSelectedInterviewerRows([]);
  };

  //  No API call here — just save interviewers to state map
  const handleSaveInterviewers = () => {
    if (selectedInterviewerKeys.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Interviewer Selected",
        text: "Please select at least one interviewer.",
        // showConfirmButton: true,
        // confirmButtonText: "OK",
      });
      return;
    }

    setInterviewerMap((prev) => ({
      ...prev,
      [currentRecord.resume_id]: selectedInterviewerKeys,
    }));

    closeBtnRef.current?.click();

    Swal.fire({
      icon: "success",
      title: "Interviewers Saved",
      text: `${selectedInterviewerKeys.length} interviewer(s) saved. They will be assigned on CV action.`,
      timer: 1800,
      showConfirmButton: false,
    });
  };

  // 👇 API calls happen HERE — accept/reject with interviewers in payload
  const handleCvAction = async (
    record: any,
    status: "Approved" | "Rejected",
  ) => {
    const isReject = status === "Rejected";
    const assignedInterviewers = interviewerMap[record.resume_id] ?? [];

    // console.log("Response :", record.mpr_status);
    if (record.mpr_status === "Closed") {
      Swal.fire({
        icon: "info",
        title: "MPR Fulfilled",
        text: "This manpower requisition has already been fulfilled",
        showConfirmButton: true,
        reverseButtons: true,
      });
      return;
    }

    if (!isReject && assignedInterviewers.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Interviewers Assigned",
        text: "Please select at least one interviewer before accepting this CV.",
      });
      return;
    }

    const result = await Swal.fire({
      title: isReject ? "Reject this CV?" : "Accept this CV?",
      text: isReject
        ? "This CV will be marked as rejected."
        : `This CV will be forwarded with ${assignedInterviewers.length} interviewer(s).`,
      icon: isReject ? "warning" : "question",
      showCancelButton: true,
      confirmButtonText: isReject ? "Yes, reject" : "Yes, accept",
      cancelButtonText: "Cancel",
      confirmButtonColor: isReject ? "#d33" : "#3085d6",
      cancelButtonColor: "#6c757d",
    });

    if (!result.isConfirmed) return;

    try {
      // Single payload for BOTH accept and reject
      const payload = {
        mr_id: record?.mr_id,
        mpr_id: record?.mpr_id,
        resume_id: record?.resume_id,
        interview_id: isReject ? [] : assignedInterviewers, // empty array on reject
        actioned_by: record?.actioned_by ?? "HR",
        status,
      };

      const response = await SelectCvApi(payload); // same API for both
      if (!response?.success) throw new Error("Action failed");

      // Cleanup & refresh
      setInterviewerMap((prev) => {
        const updated = { ...prev };
        delete updated[record.resume_id];
        return updated;
      });

      refetchResumes?.();

      Swal.fire({
        icon: "success",
        title: isReject ? "Rejected!" : "Accepted!",
        text: `CV has been ${status} successfully.`,
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Action error:", error);
      Swal.fire({
        icon: "error",
        title: "Failed!",
        text: "Something went wrong. Please try again.",
      });
    }
  };
  const columnInterviewer = [
    {
      title: "S.no",
      key: "sno",
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: "Employee Name",
      dataIndex: "employee_name",
      key: "employee_name",
    },
    { title: "Employee Id", dataIndex: "employee_id", key: "employee_id" },
    { title: "Department", dataIndex: "department", key: "department" },
    { title: "Designation", dataIndex: "designation", key: "designation" },
    { title: "Location", dataIndex: "location", key: "location" },
  ];

  const column = [
    {
      title: "CV Id",
      dataIndex: "resume_id",
      key: "resume_id",
      render: (_: string) => <div className="text-nowrap">{_}</div>,
    },
    {
      title: "MPR Id",
      dataIndex: "mpr_id",
      key: "mpr_id",
      render: (text: string) => (
        <Link
          to={`/man-power-recruitment/${text}`}
          style={{
            textDecoration: "underline",
            color: "#0d6efd",
            fontWeight: 500,
            backgroundColor: "#e7f1ff",
            padding: "2px 4px",
            borderRadius: "4px",
          }}
          className="text-nowrap"
        >
          {text}
        </Link>
      ),
    },
    {
      title: "Applicant Name",
      dataIndex: "candidate_name",
      key: "candidate_name",
      render: (text: string) => <div className="text-nowrap">{text}</div>,
    },
    {
      title: "Applicant Mail",
      dataIndex: "candidate_email",
      key: "candidate_email",
      render: (text: string) => <div className="text-nowrap">{text}</div>,
    },
    {
      title: "Profile Name",
      dataIndex: "profile_name",
      key: "profile_name",
      render: (text: string) => <div className="text-nowrap">{text}</div>,
    },
    {
      title: "Position Name",
      dataIndex: "position_name",
      key: "position_name",
      render: (text: string) => <div className="text-nowrap">{text}</div>,
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      render: (text: string) => <div className="text-nowrap">{text}</div>,
    },
    {
      title: "CTC",
      dataIndex: "ctc",
      key: "ctc",
      render: (_: string) => <div className="text-nowrap">{_}</div>,
    },
    {
      title: "Approved By",
      dataIndex: "actioned_by",
      key: "actioned_by",
      align: "center" as const,
      // render: (text: string) => (
      //   <span className="text-muted text-nowrap">{text}</span>
      // ),
    },
    {
      title: "Received",
      dataIndex: "created_at",
      key: "created_at",
      align: "center" as const,
      render: (date: string) => (
        <span className="text-muted text-nowrap">
          {new Date(date).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "long",
            year: "numeric",
          })}
        </span>
      ),
    },
    {
      title: "HR Comments",
      dataIndex: "hr_comments",
      key: "hr_comments",
      align: "center" as const,
      render: (text: string) => (
        <span className="text-muted text-nowrap">{text ?? "—"}</span>
      ),
    },
    {
      title: "View CV",
      dataIndex: "resume",
      key: "view_cv",
      align: "center" as const,
      render: (text: any) => (
        <div className="d-flex justify-content-center">
          <Link to={text} target="_blank" className="text-info">
            <i className="ti ti-eye" />
          </Link>
        </div>
      ),
    },
    {
      title: "Interviewer",
      key: "interviewer",
      align: "center" as const,
      render: (_: any, record: any) => {
        const count = interviewerMap[record.resume_id]?.length ?? 0;
        return (
          <div className="d-flex justify-content-center">
            <button
              className={`btn btn-sm text-truncate btn-outline-primary`}
              data-bs-toggle="modal"
              data-bs-target="#selectInterviewerModal"
              onClick={() => handleOpenModal(record)}
              style={{ width: "100px" }}
              disabled={record.mpr_status === "Closed"}
            >
              {/* 👇 Show count badge if already selected */}
              {count > 0 ? ` ${count} Selected` : "Select Interviewer"}
            </button>
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center" as const,
      sorter: (a: any, b: any) => a.status.localeCompare(b.status),
      render: (status: string) => (
        <span
          className={`badge ${status === "Approved" ? "bg-success" : "bg-warning text-dark"}`}
          style={{
            fontSize: "12px",
            padding: "4px 10px",
            fontWeight: 500,
            minWidth: "80px",
            textAlign: "center",
            display: "inline-block",
          }}
        >
          {status}
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "center" as const,
      render: (_: any, record: any) => {
        const isActioned =
          record.status === "Approved" || record.status === "Rejected";

        return (
          <div
            className="d-flex justify-content-center gap-2"
            style={{ cursor: isActioned ? "not-allowed" : "pointer" }}
          >
            <button
              className="btn btn-sm border-0"
              title="Accept CV"
              disabled={isActioned || statusUpdating || submitting}
              onClick={() => handleCvAction(record, "Approved")}
            >
              <span className="text-success">
                <i className="ti ti-circle-check"></i>
              </span>
            </button>
            <button
              className="btn btn-sm border-0"
              title="Reject CV"
              disabled={isActioned || statusUpdating || submitting}
              onClick={() => handleCvAction(record, "Rejected")}
            >
              <span className="text-danger">
                <i className="ti ti-circle-x"></i>
              </span>
            </button>
          </div>
        );
      },
    },
  ];

  const isLocked =
    currentRecord?.status === "Approved" ||
    currentRecord?.status === "Rejected";

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Candidate Selection</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Recruitment</li>
                  <li className="breadcrumb-item active">
                    Candidate Selection
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <CollapseHeader />
            </div>
          </div>

          <div className="card">
            <div className="card-body px-0">
              <Table
                dataSource={resumeList?.data}
                columns={column}
                Selection={false}
                showSearchInput={true}
              />
            </div>
          </div>
        </div>

        <div className="footer d-flex align-items-center justify-content-center border-top bg-white p-3">
          <p className="mb-0">© {new Date().getFullYear()} WeConnect. All rights reserved.</p>
        </div>
      </div>

      {/* ===================== Interviewer Modal ===================== */}
      <div
        className="modal fade"
        id="selectInterviewerModal"
        tabIndex={-1}
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-xl">
          <div className="modal-content">
            <div className="modal-header">
              <div>
                <h5 className="modal-title mb-0">Select Interviewers</h5>
                {currentRecord && (
                  <small className="text-muted">
                    CV: <strong>{currentRecord.resume_id}</strong>
                    &nbsp;|&nbsp; Position:{" "}
                    <strong>{currentRecord.position_name}</strong>
                  </small>
                )}
              </div>
              <button
                ref={closeBtnRef}
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
              />
            </div>

            <div className="modal-body p-0">
              <div className="px-3 pt-3 d-flex align-items-center gap-2">
                <span
                  className="text-info px-3 py-2"
                  style={{ backgroundColor: "#cce5ff", borderRadius: "8px" }}
                >
                  {selectedInterviewerKeys.length} / 4 interviewers selected
                </span>
                {selectedInterviewerKeys.length > 0 && (
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => {
                      setSelectedInterviewerKeys([]);
                      setSelectedInterviewerRows([]);
                    }}
                    disabled={isLocked}
                    style={{
                      cursor: isLocked ? "not-allowed" : "pointer",
                    }}
                  >
                    Clear
                  </button>
                )}
              </div>

              <MaxSelectTable
                dataSource={employeeList?.data ?? []}
                columns={columnInterviewer}
                rowKey="employee_id"
                maxSelect={4}
                disabled={isLocked}
                showSearchInput={true}
                selectedKeys={selectedInterviewerKeys} // 👈 pass pre-selected keys if your component supports it
                onSelectionChange={(keys, rows) => {
                  if (isLocked) return;

                  setSelectedInterviewerKeys(keys);
                  setSelectedInterviewerRows(rows);
                }}
              />
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" data-bs-dismiss="modal">
                Cancel
              </button>
              {/* 👇 "Save" instead of "Assign" — no API call */}
              <button
                className="btn btn-primary mx-2"
                onClick={handleSaveInterviewers}
                disabled={selectedInterviewerKeys.length === 0 || isLocked}
                style={{
                  cursor: isLocked ? "not-allowed" : "pointer",
                }}
              >
                {isLocked
                  ? `Already ${currentRecord?.status}`
                  : `Save Interviewers (${selectedInterviewerKeys.length})`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PreviewResume;
